import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RegistrationsTableTitle } from '../../molecules/RegistrationsTableTitle/RegistrationsTableTitle';
import {
  getRegistrations,
  updateRegistration,
  getRegistrationsExport,
  RegistrationStatus,
} from '@services/registrations';
import type { Registration } from '@services/registrations';
import { DataTable } from '@components/assessors/molecules/DataTable/DataTable';
import { DataTablePaginator } from '@components/assessors/molecules/DataTablePaginator/DataTablePaginator';
import { Button, Text } from '@acb_devel/design-system';
import { Field } from '@acb_devel/design-system';
import { DownloadIcon } from '@acb_devel/design-system';
import { ProvincesFilter } from '@components/assessors/molecules/ProvincesFilter/ProvincesFilter';
import { EventFilter } from '@components/assessors/molecules/EventFilter/EventFilter';
import type { Event } from '@services/events';
import { RegistrationStatusFilter } from '@components/assessors/molecules/RegistrationStatusFilter/RegistrationStatusFilter';
import { RegistrationStatusSwitch } from '@components/assessors/molecules/RegistrationStatusSwitch/RegistrationStatusSwitch';
import { useModal } from '@acb_devel/design-system';
import { SelectAreasModal } from '@components/assessors/molecules/SelectAreasModal/SelectAreasModal';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system';
import { Spinner } from '@acb_devel/design-system';
import { useEventsContext } from '@contexts/EventsContext';
import { SearchIcon } from '@acb_devel/design-system';

interface Filters {
  page: number;
  search: string;
  provinces: string[];
  event: Event | null;
  status: string[];
  sortKey: string;
  sortDirection: 'asc' | 'desc';
}

import styles from './RegistrationsTable.module.scss';

export function RegistrationsTable() {
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const { events } = useEventsContext();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // datatable
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [sortKey, setSortKey] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // filters
  const [search, setSearch] = useState<string>('');
  const [provinces, setProvinces] = useState<string[]>([]);
  const [event, setEvent] = useState<Event | null>(null);
  const [status, setStatus] = useState<string[]>([]);

  const { isOpen, openModal, closeModal } = useModal();
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);
  const [loadingDownload, setLoadingDownload] = useState(false);

  function loadRegistrations() {
    setLoadingRegistrations(true);
    getRegistrations({
      offset: (page - 1) * pageSize,
      limit: pageSize,
      search,
      ordering: sortKey
        ? `${sortDirection === 'asc' ? '' : '-'}${sortKey}`
        : '',
      provinces,
      eventId: event?.id,
      status,
    })
      .then((registrations) => {
        setRegistrations(registrations.results);
        setTotalRegistrations(registrations.count);
      })
      .finally(() => {
        setLoadingRegistrations(false);
      });
  }

  useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    // Guardar en sessionStorage cada vez que cambien los filtros
    const filters: Filters = {
      page,
      search,
      provinces,
      event,
      status,
      sortKey,
      sortDirection,
    };

    sessionStorage.setItem(
      'registrationsDatatableFilters',
      JSON.stringify(filters)
    );

    loadRegistrations();
  }, [page, search, provinces, event, status, sortKey, sortDirection]);

  useEffect(() => {
    if (isInitialLoad || loadingRegistrations) {
      return;
    }

    if ((page - 1) * pageSize >= totalRegistrations) {
      setPage(1);
    }
  }, [page, pageSize, totalRegistrations, isInitialLoad]);

  useEffect(() => {
    const storedFilters = sessionStorage.getItem(
      'registrationsDatatableFilters'
    );

    if (storedFilters) {
      const filters: Filters = JSON.parse(storedFilters) as Filters;

      setSearch(filters.search);
      setProvinces(filters.provinces);
      setEvent(filters.event);
      setStatus(filters.status);
      setSortKey(filters.sortKey);
      setSortDirection(filters.sortDirection);
      setPage(filters.page);
    } else {
      loadRegistrations();
    }

    setIsInitialLoad(false);
  }, []);

  useEffect(() => {
    if (events.length > 0 && !event && !isInitialLoad) {
      setEvent(events[0]);
    }
  }, [events]);

  function handleStatusChange(
    registration: Registration,
    status: RegistrationStatus
  ) {
    if (
      status === RegistrationStatus.CONFIRMED &&
      registration.event.eventAreas.length > 0
    ) {
      setSelectedRegistration(registration);
      openModal();
      return;
    }

    handleUpdateRegistration(registration, status);
  }

  function handleSelectedAreasSubmit(registration: Registration | null) {
    if (registration === null) {
      closeModal();
      return;
    }

    handleUpdateRegistration(registration, RegistrationStatus.CONFIRMED);
    closeModal();
  }

  function handleUpdateRegistration(
    registration: Registration,
    status: RegistrationStatus
  ) {
    if (registration.id === null) {
      return;
    }

    updateRegistration({
      registrationId: registration.id,
      status,
      assignedEventAreas: registration.assignedEventAreas.map(
        (area) => area.id
      ),
    })
      .then(() => {
        setRegistrations((registrations) =>
          registrations.map((r) =>
            r.id === registration.id ? { ...r, ...registration, status } : r
          )
        );
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al actualizar la inscripción`}
              text="Ha ocurrido un error al actualizar la inscripción. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );
        console.error(error);
      });
  }

  function handleDownloadRegistrations() {
    setLoadingDownload(true);
    getRegistrationsExport({
      offset: (page - 1) * pageSize,
      limit: pageSize,
      search,
      provinces,
      eventId: event?.id,
      status,
    })
      .then((data) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'inscripciones.csv');
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al descargar las inscripciones`}
              text="Ha ocurrido un error al descargar las inscripciones. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );
        console.error(error);
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  }

  if (isInitialLoad) {
    return <Spinner />;
  }

  return (
    <div className={styles.registrationsTable}>
      <RegistrationsTableTitle />
      <div className={styles.registrationsTable__tableHeader}>
        <div className={styles.registrationsTable__filters}>
          <Field
            id="search"
            label=""
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por..."
            prependSlot={<SearchIcon />}
          />
          <ProvincesFilter provinces={provinces} setProvinces={setProvinces} />
          <EventFilter event={event} setEvent={setEvent} />
          <RegistrationStatusFilter status={status} setStatus={setStatus} />
        </div>
        <Button
          variant="solid"
          color="primary"
          onClick={handleDownloadRegistrations}
          disabled={loadingDownload}
        >
          <Spinner loading={loadingDownload}>
            <DownloadIcon />
          </Spinner>
          Descargar
        </Button>
      </div>
      <DataTable
        data={registrations}
        onRowClick={(row) => navigate(`/user/admin/users/${row.user.id}`)}
        sortKey={sortKey}
        direction={sortDirection}
        loading={loadingRegistrations}
        onSort={(sortKey, sortOrder) => {
          setSortKey(sortKey);
          setSortDirection(sortOrder);
        }}
        columns={[
          {
            sortKey: 'id',
            label: 'ID',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.id.toString()}
              </Text>
            ),
          },
          {
            sortKey: 'volunteer_code',
            label: 'Código voluntario',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.profile?.volunteerCode?.toString() ?? '-'}
              </Text>
            ),
          },
          {
            sortKey: 'first_name',
            label: 'Nombre',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.profile?.firstName ?? ''}
              </Text>
            ),
          },
          {
            sortKey: 'last_name',
            label: 'Apellidos',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.profile?.lastName ?? ''}
              </Text>
            ),
          },
          {
            sortKey: 'email_address',
            label: 'Email',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.emailAddress}
              </Text>
            ),
          },
          {
            sortKey: 'city',
            label: 'Ciudad',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.profile?.city ?? ''}
              </Text>
            ),
          },
          {
            sortKey: 'province',
            label: 'Provincia',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.user.profile?.province ?? ''}
              </Text>
            ),
          },
          {
            sortKey: 'event_id',
            label: 'Evento',
            render: (row) => (
              <Text variant="body-2" as="span">
                {row.event.fullName}
              </Text>
            ),
          },
          {
            sortKey: 'status',
            label: 'Estado',
            render: (row) => (
              <div onClick={(event) => event.stopPropagation()}>
                <RegistrationStatusSwitch
                  registration={row}
                  onStatusChange={handleStatusChange}
                />
              </div>
            ),
          },
        ]}
      />
      <DataTablePaginator
        totalRows={totalRegistrations}
        perPage={pageSize}
        page={page}
        setPage={setPage}
        className={styles.paginator}
      />
      <SelectAreasModal
        isOpen={isOpen}
        closeModal={closeModal}
        registration={selectedRegistration}
        setRegistration={setSelectedRegistration}
        onSubmitted={handleSelectedAreasSubmit}
      />
    </div>
  );
}
